import { ref, computed } from 'vue';
import {
  startOfWeek,
  endOfWeek,
  startOfDay,
  endOfDay,
  addDays,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  format,
} from 'date-fns';
import ContactAPI from 'dashboard/api/contacts';
import { getWeeksForMonth } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import { getFollowUpUrgency } from 'dashboard/components-next/FollowUp/composables/useFollowUp.js';

// The Calendar is a read-only view over the Follow-up Engine's own data —
// it fetches contacts by next_follow_up_at the exact same way Kanban
// already fetches contacts by stage (ContactAPI.filter with custom
// attribute conditions). No new endpoint, no event data stored anywhere.
const VIEW_MODES = ['month', 'week', 'day', 'agenda'];
const PAGE_SIZE_SAFETY_LIMIT = 20; // guards against runaway pagination loops

export function useCalendar() {
  const viewMode = ref('month');
  const currentDate = ref(new Date());
  const events = ref([]);
  const isLoading = ref(false);

  const monthGridWeeks = computed(() => getWeeksForMonth(currentDate.value));

  const visibleRange = computed(() => {
    switch (viewMode.value) {
      case 'month': {
        const weeks = monthGridWeeks.value;
        return {
          start: startOfDay(weeks[0][0]),
          end: endOfDay(weeks[weeks.length - 1][6]),
        };
      }
      case 'week':
        return {
          start: startOfWeek(currentDate.value, { weekStartsOn: 1 }),
          end: endOfWeek(currentDate.value, { weekStartsOn: 1 }),
        };
      case 'day':
        return {
          start: startOfDay(currentDate.value),
          end: endOfDay(currentDate.value),
        };
      // Agenda: a rolling 30-day look-ahead of actionable follow-ups.
      case 'agenda':
      default:
        return {
          start: startOfDay(currentDate.value),
          end: endOfDay(addDays(currentDate.value, 30)),
        };
    }
  });

  const buildDateRangeConditions = ({ start, end }) => ({
    payload: [
      {
        attribute_key: 'next_follow_up_at',
        attribute_model: 'contact_attribute',
        filter_operator: 'is_greater_than',
        values: [format(addDays(start, -1), 'yyyy-MM-dd')],
        query_operator: 'and',
      },
      {
        attribute_key: 'next_follow_up_at',
        attribute_model: 'contact_attribute',
        filter_operator: 'is_less_than',
        values: [format(addDays(end, 1), 'yyyy-MM-dd')],
      },
    ],
  });

  const toEvent = contact => ({
    contact,
    date: new Date(contact.custom_attributes.next_follow_up_at),
    urgency: getFollowUpUrgency(contact.custom_attributes.next_follow_up_at),
    isMeeting: !!contact.custom_attributes.meeting_required,
  });

  const fetchEvents = async () => {
    isLoading.value = true;
    const range = visibleRange.value;
    const conditions = buildDateRangeConditions(range);

    try {
      const contacts = [];
      let page = 1;
      let hasMore = true;

      while (hasMore && page <= PAGE_SIZE_SAFETY_LIMIT) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await ContactAPI.filter(
          page,
          '-created_at',
          conditions
        );
        contacts.push(...data.payload);
        hasMore = data.payload.length === 15;
        page += 1;
      }

      events.value = contacts
        .filter(contact => contact.custom_attributes?.next_follow_up_at)
        .map(toEvent);
    } finally {
      isLoading.value = false;
    }
  };

  const setViewMode = mode => {
    if (!VIEW_MODES.includes(mode)) return;
    viewMode.value = mode;
    fetchEvents();
  };

  const goToday = () => {
    currentDate.value = new Date();
    fetchEvents();
  };

  const goNext = () => {
    if (viewMode.value === 'month') {
      currentDate.value = addMonths(currentDate.value, 1);
    } else if (viewMode.value === 'week') {
      currentDate.value = addWeeks(currentDate.value, 1);
    } else {
      currentDate.value = addDays(currentDate.value, 1);
    }
    fetchEvents();
  };

  const goPrev = () => {
    if (viewMode.value === 'month') {
      currentDate.value = subMonths(currentDate.value, 1);
    } else if (viewMode.value === 'week') {
      currentDate.value = subWeeks(currentDate.value, 1);
    } else {
      currentDate.value = addDays(currentDate.value, -1);
    }
    fetchEvents();
  };

  const eventsForDay = day =>
    events.value.filter(
      event =>
        event.date.getFullYear() === day.getFullYear() &&
        event.date.getMonth() === day.getMonth() &&
        event.date.getDate() === day.getDate()
    );

  return {
    viewMode,
    currentDate,
    monthGridWeeks,
    visibleRange,
    events,
    isLoading,
    setViewMode,
    goToday,
    goNext,
    goPrev,
    fetchEvents,
    eventsForDay,
  };
}
